import { useCallback } from 'react'

import type { ApolloCache } from '@apollo/client'
import { useApolloClient } from '@apollo/client'
import { MarketplaceDbIntegration } from 'generated/resources/datatable.graphql'
import {
  IntegrationsSyncRunFragment,
  IntegrationsSyncRunParameterFragment,
  IntegrationsSyncRunStatusChangeSubscriptionFragment,
  LastSyncRunDocument,
} from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'

import { type ContextParameters } from 'modules/integrations/hooks/SyncContext'

interface UpdateLastSyncRunQueryCacheProps {
  marketplaceIntegration: MarketplaceDbIntegration
  contextParameters: ContextParameters
  syncRun: IntegrationsSyncRunFragment | IntegrationsSyncRunStatusChangeSubscriptionFragment
}

interface ReadLastSyncRunProps {
  marketplaceIntegration: MarketplaceDbIntegration | null
  contextParameters: ContextParameters
  syncEnabled: boolean
}

interface SyncRunParameter {
  key: string
  value: string
}

interface LastSyncRunQueryVariables {
  marketplaceIntegrationId: number
  parameters: SyncRunParameter[]
}

const contextParametersToArray = (params: ContextParameters) => {
  return Object.entries(params).map(([key, value]) => ({ key, value }))
}

export const buildLastSyncRunVariables = (
  marketplaceIntegrationId: number,
  contextParameters: ContextParameters
): LastSyncRunQueryVariables => {
  return {
    marketplaceIntegrationId: marketplaceIntegrationId,
    parameters: contextParametersToArray(contextParameters),
  }
}

export const writeLastSyncRunQueryCache = (
  cache: ApolloCache<unknown>,
  { marketplaceIntegration, contextParameters, syncRun }: UpdateLastSyncRunQueryCacheProps
) => {
  cache.writeQuery({
    query: LastSyncRunDocument,
    variables: buildLastSyncRunVariables(marketplaceIntegration.id, contextParameters),
    data: {
      integrations: {
        syncRunsConnection: {
          nodes: [
            {
              id: syncRun.id,
              finishedAt: syncRun.finishedAt,
              status: syncRun.status,
              parameters: buildSyncRunParameters(syncRun.parameters),
              __typename: 'IntegrationsSyncRun',
            },
          ],
          __typename: 'IntegrationsSyncRunConnection',
        },
        __typename: 'integrations',
      },
    },
  })
}

const buildSyncRunParameters = (
  parameters: SyncRunParameter[]
): IntegrationsSyncRunParameterFragment[] => {
  return parameters.map((param) => ({
    key: param.key,
    value: param.value,
    __typename: 'IntegrationsSyncRunParameters',
  }))
}

export const useUpdateLastSyncRunQueryCache = () => {
  const client = useApolloClient()

  return useCallback(
    (props: UpdateLastSyncRunQueryCacheProps) => {
      writeLastSyncRunQueryCache(client.cache, props)
    },
    [client]
  )
}

export const useReadLastSyncRun = ({
  marketplaceIntegration,
  contextParameters,
  syncEnabled,
}: ReadLastSyncRunProps) => {
  return useQuery(LastSyncRunDocument, {
    variables: buildLastSyncRunVariables(marketplaceIntegration?.id || 0, contextParameters),
    skip: !syncEnabled,
  })
}
